import React, { useEffect, useState, useCallback } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  UsersThreeIcon,
  CrownSimpleIcon,
  ShieldCheckIcon,
  ArrowClockwiseIcon,
  ProhibitIcon,
} from "@phosphor-icons/react";
import SellerAvatar from "@/components/SellerAvatar";

/**
 * Seller-only popover that reveals the live list of people watching the
 * stream. Triggered by clicking the viewer-count pill over the video.
 *
 * Props:
 *   streamId   – string, current stream id.
 *   liveCount  – number, fast WS-driven viewer count (shown in the pill).
 *   enabled    – bool, gate interactivity (true only for seller / admin).
 *   refreshKey – any, bump this to force a re-fetch (e.g. pass presence tick).
 */
export default function ViewerListPopover({
  streamId,
  liveCount,
  enabled,
  refreshKey,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [kickTarget, setKickTarget] = useState(null); // {user_id, user_name}
  const [kicking, setKicking] = useState(false);

  const fetchViewers = useCallback(async () => {
    if (!streamId) return;
    setLoading(true);
    setError("");
    try {
      const { data: resp } = await api.get(`/streams/${streamId}/viewers`);
      setData(resp);
    } catch (err) {
      setError(apiError(err) || "Unable to load viewers");
    } finally {
      setLoading(false);
    }
  }, [streamId]);

  const handleKick = async () => {
    if (!kickTarget) return;
    setKicking(true);
    try {
      const { data: resp } = await api.post(
        `/streams/${streamId}/kick/${kickTarget.user_id}`
      );
      toast.success(
        `${kickTarget.user_name} removed (${resp.connections_closed} connection${
          resp.connections_closed === 1 ? "" : "s"
        } closed)`
      );
      setKickTarget(null);
      await fetchViewers();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setKicking(false);
    }
  };

  // Re-fetch when opening or when presence ticks arrive (join/leave).
  useEffect(() => {
    if (open) fetchViewers();
  }, [open, refreshKey, fetchViewers]);

  // Non-seller fast path: render a plain read-only pill — no trigger.
  if (!enabled) {
    return (
      <div
        className="absolute top-3 right-3 pill pill-dark z-10"
        data-testid="viewer-count-readonly"
      >
        <UsersThreeIcon size={12} /> {liveCount}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="absolute top-3 right-3 pill pill-dark z-10 cursor-pointer hover:bg-zinc-800 transition-colors focus:outline-none focus:ring-2 focus:ring-[#1D78AB]/50"
          title="Tap to see who's watching"
          data-testid="viewer-count-btn"
        >
          <UsersThreeIcon size={12} weight="fill" /> {liveCount}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0 overflow-hidden border-zinc-200 shadow-xl"
        data-testid="viewer-list-popover"
      >
        <div className="bg-zinc-900 text-white px-4 py-3 flex items-center justify-between">
          <div>
            <div className="font-display font-black text-sm tracking-tight">
              Watching right now
            </div>
            <div className="text-[11px] text-zinc-400 mt-0.5">
              {data
                ? `${data.unique_users} named · ${data.anonymous} anonymous`
                : "Loading…"}
            </div>
          </div>
          <button
            type="button"
            onClick={fetchViewers}
            disabled={loading}
            className="text-zinc-400 hover:text-white disabled:opacity-50 p-1 rounded-full"
            title="Refresh"
            data-testid="viewer-list-refresh"
          >
            <ArrowClockwiseIcon
              size={16}
              weight="bold"
              className={loading ? "animate-spin" : ""}
            />
          </button>
        </div>

        <div
          className="max-h-80 overflow-y-auto divide-y divide-zinc-100"
          data-testid="viewer-list-body"
        >
          {error && (
            <div
              className="p-4 text-xs text-red-600"
              data-testid="viewer-list-error"
            >
              {error}
            </div>
          )}
          {!error && !loading && data && data.viewers.length === 0 && (
            <div
              className="p-6 text-center text-xs text-zinc-500"
              data-testid="viewer-list-empty"
            >
              No signed-in viewers yet.
              {data.anonymous > 0 && (
                <div className="mt-1 text-zinc-400">
                  {data.anonymous} anonymous watcher
                  {data.anonymous === 1 ? "" : "s"} currently connected.
                </div>
              )}
            </div>
          )}
          {data &&
            data.viewers.map((v) => (
              <ViewerRow
                key={v.conn_id}
                viewer={v}
                onKick={
                  v.is_seller ? null : () =>
                    setKickTarget({
                      user_id: v.user_id,
                      user_name: v.user_name || "this viewer",
                    })
                }
              />
            ))}
          {data && data.anonymous > 0 && data.viewers.length > 0 && (
            <div
              className="px-4 py-2.5 text-[11px] text-zinc-500 bg-zinc-50"
              data-testid="viewer-list-anon-footer"
            >
              + {data.anonymous} anonymous viewer
              {data.anonymous === 1 ? "" : "s"} (not signed in)
            </div>
          )}
        </div>

        <div className="px-4 py-2.5 bg-zinc-50 border-t border-zinc-200 text-[10px] text-zinc-500 leading-snug">
          Only you (the seller) can see this list. Buyers never know you're
          looking.
        </div>
      </PopoverContent>

      <AlertDialog
        open={!!kickTarget}
        onOpenChange={(o) => !o && !kicking && setKickTarget(null)}
      >
        <AlertDialogContent data-testid="kick-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {kickTarget?.user_name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They'll be disconnected immediately and banned from rejoining
              this stream. Their bids and chat history remain. You can lift
              the ban later if you change your mind.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={kicking}
              data-testid="kick-confirm-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={kicking}
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleKick}
              data-testid="kick-confirm-remove"
            >
              {kicking ? "Removing…" : "Remove & ban"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Popover>
  );
}

function ViewerRow({ viewer, onKick }) {
  const joined = viewer.joined_at ? new Date(viewer.joined_at) : null;
  const mins = joined
    ? Math.max(0, Math.round((Date.now() - joined.getTime()) / 60000))
    : null;
  const timeLabel =
    mins === null ? "" : mins < 1 ? "just now" : `${mins}m watching`;
  const roleBadge = viewer.is_seller ? (
    <span
      className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full"
      data-testid="viewer-row-seller"
    >
      <CrownSimpleIcon size={10} weight="fill" /> Seller
    </span>
  ) : viewer.is_admin ? (
    <span
      className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold text-[#1D78AB] bg-[#1D78AB]/10 px-1.5 py-0.5 rounded-full"
      data-testid="viewer-row-admin"
    >
      <ShieldCheckIcon size={10} weight="fill" /> Admin
    </span>
  ) : null;

  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-50"
      data-testid={`viewer-row-${viewer.user_id}`}
    >
      <SellerAvatar
        name={viewer.user_name || "Bidorah fan"}
        picture={viewer.picture}
        size={34}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <div
            className="text-sm font-semibold text-zinc-900 truncate max-w-[140px]"
            data-testid={`viewer-row-name-${viewer.user_id}`}
          >
            {viewer.user_name || "Bidorah fan"}
          </div>
          {roleBadge}
        </div>
        <div className="text-[11px] text-zinc-500 truncate">
          {viewer.username ? `@${viewer.username} · ` : ""}
          {timeLabel}
        </div>
      </div>
      {onKick && (
        <button
          type="button"
          onClick={onKick}
          title="Remove & ban from stream"
          className="opacity-70 hover:opacity-100 text-zinc-500 hover:text-red-600 transition p-1.5 rounded-full hover:bg-red-50"
          data-testid={`viewer-row-kick-${viewer.user_id}`}
        >
          <ProhibitIcon size={16} weight="bold" />
        </button>
      )}
    </div>
  );
}
